import React, { useEffect, useState } from 'react';
import styles from './ExperiencePage.module.css';

export default function ExperiencePage({ data }) {
  const [activeCategory, setActiveCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!selectedImage) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setSelectedImage(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedImage]);

  // ORDERED EXPERIENCES:
  // 1. Research Experience (AAUB Research Assistant) FIRST
  // 2. Research Mentor (ResearchBuddy AI) SECOND
  // Followed by Industrial, R&D Leadership, and Technical/Club Leadership
  const experiences = [
    {
      category: "Research Experience",
      categoryBadge: "🔬 Research Experience",
      badgeStyle: styles.badgeTeal,
      organization: "Aviation and Aerospace University, Bangladesh (AAUB)",
      role: "Research Assistant — AI Toolkit for Predictive Maintenance of Aircraft Systems",
      duration: "Feb 2026 – Present",
      location: "Dhaka, Bangladesh",
      highlight: "Funded Research Project · PI: Air Cdre Dr. Saifur Rahman Bakaul",
      description: [
        "Contribute to the funded project \"Development of an Artificial Intelligence Toolkit Based on Machine Learning for Predictive Maintenance of Aircraft Systems\" under Principal Investigator Air Cdre Dr. Saifur Rahman Bakaul.",
        "Carry out data preprocessing, machine learning model development, and validation for aircraft system health monitoring and prognostics workflows.",
        "Bridge the gap between research models and deployable maintenance tools, building directly on my BSc thesis on turbofan engine fault diagnosis and remaining useful life prediction."
      ],
      logo: "/assets/experience/aaub_logo.png",
      images: [],
      link: "https://www.aaub.edu.bd/",
      source: "Official Website",
      tags: ["Predictive Maintenance", "Machine Learning", "Prognostics", "Data Preprocessing", "Model Validation"]
    },
    {
      category: "Research Mentorship",
      categoryBadge: "🎓 Research Mentor",
      badgeStyle: styles.badgeBlue,
      organization: "ResearchBuddy AI — Intelligent System Engineering Lab",
      role: "Research Mentor",
      duration: "Jun 2026 – Present",
      location: "Bangladesh",
      highlight: "Academic Research Mentorship · Mentoring 10–15 Undergraduate Researchers",
      description: [
        "Mentor 10–15 students at the beginning of their research journey, providing step-by-step guidance on how to start and conduct academic research.",
        "Guide students in identifying research topics, reviewing literature, formulating research questions, and planning appropriate research methods."
      ],
      logo: "",
      logoFallback: "RB",
      images: [],
      tags: ["Research Mentorship", "Literature Review", "Research Methodology", "Academic Supervision"]
    },
    {
      category: "Industrial Experience",
      categoryBadge: "✈️ Industrial Experience",
      badgeStyle: styles.badgeEmerald,
      organization: "Biman Bangladesh Airlines",
      role: "Industrial Intern — Maintenance Operations",
      duration: "Jun 2024 – Jul 2024",
      location: "Dhaka, Bangladesh",
      highlight: "Widebody & Regional Fleet MRO · Boeing 787, 777, 737 & Dash 8",
      description: [
        "Completed intensive 1‑month internship with maintenance teams across Boeing 787, 777, 737 and Dash 8 aircraft, gaining operational exposure across widebody and regional platforms.",
        "Observed and assisted with 40+ maintenance operations spanning aircraft inspections, system diagnostics, component replacement and technical documentation analysis.",
        "Developed an operator's perspective on the gap between research prototypes and hangar-floor reality — directly informing my PhD interest in physics-informed structural health monitoring and predictive maintenance."
      ],
      logo: "/assets/experience/biman.png",
      images: [
        { image: "/assets/experience/biman_2.jpg", caption: "Flight Line Maintenance Operations" },
        { image: "/assets/experience/biman_3.jpg", caption: "Aircraft Systems Inspection" }
      ],
      link: "https://www.biman-airlines.com/",
      source: "Official Website",
      tags: ["MRO Operations", "Regulatory Compliance", "Operational Research", "Systems Diagnostics"]
    },
    {
      category: "Leadership & R&D",
      categoryBadge: "🚀 Research & R&D Leadership",
      badgeStyle: styles.badgePurple,
      organization: "Frontier Alliance",
      role: "Founder & Director",
      duration: "Oct 2024 – Present",
      location: "Lalmonirhat, Bangladesh",
      highlight: "Multidisciplinary Aerospace R&D Organization · 10+ Cross-Functional Members",
      description: [
        "Founded and direct a multidisciplinary research organization focused on UAV technology, aerospace innovation, and technical education.",
        "Coordinate cross-functional teams of 10+ members for AIAA Design Competitions and NASA Space Apps Challenge, managing project timelines, peer-review workflows, and deliverables.",
        "Guide strategic partnerships and outreach initiatives to drive innovation in aerospace technology and humanitarian applications, including expert webinars with Airbus senior leaders."
      ],
      logo: "/assets/experience/frontier.jpg",
      images: [
        { image: "/assets/experience/FA_founder.jpg", caption: "Meet the Founders — Executive Board" },
        { image: "/assets/experience/FA_skybee.jpg", caption: "SkyBee UAV — Final Design Report" },
        { image: "/assets/experience/FA_Presenting_Project_Homeland_Defense_Interceptor.jpg", caption: "Presenting Project Homeland Defense Interceptor" },
        { image: "/assets/experience/FA_Meeting_Members.jpg", caption: "Frontier Alliance Members Meeting & Workshop" },
        { image: "/assets/experience/FA_with_volunteers.jpg", caption: "Frontier Alliance Team & Volunteers" },
        { image: "/assets/experience/FA_Arranage_Webinar_Airbus_Expert_2026.jpg", caption: "Expert Webinar with Airbus Senior Leader" },
        { image: "/assets/experience/FA_Arranage_Webinar_Europe_2025.jpg", caption: "Higher Study Guidelines in Europe — Interactive Session" },
        { image: "/assets/experience/FA_Arranage_Webinar_Europe_2025_2.jpg", caption: "Online Webinar: European Higher Study Preparation" }
      ],
      link: "https://www.linkedin.com/company/frontier-alliance-aaub/posts/?feedView=all&viewAsMember=true",
      source: "LinkedIn Organization",
      tags: ["Executive Leadership", "Grant Writing", "Peer Review", "Project Management", "UAV R&D"]
    },
    {
      category: "Research Experience",
      categoryBadge: "🏆 Competition Research Lead",
      badgeStyle: styles.badgeGold,
      organization: "AIAA Undergraduate Team Aircraft Design Competition",
      role: "Team Manager — Research Project Lead",
      duration: "Oct 2024 – May 2025",
      location: "International",
      highlight: "3rd Place Winner (AIAA Region VII Team Category) · Best Vehicle Design 2025",
      description: [
        "Managed a 10‑person technical team through the complete aircraft design lifecycle: configuration design, trade-study analysis, mass properties estimation, and control & stability analysis.",
        "Delivered rigorous technical documentation under strict competition deadlines, earning the Best Aerospace Vehicle Design Award 2025 for the Sky Sentinel S26 supersonic interceptor.",
        "Performed comparative structural assessments and aerodynamic optimisation across competing designs, demonstrating systems engineering and deadline management under pressure."
      ],
      logo: "/assets/experience/frontier.jpg",
      images: [
        { image: "/assets/experience/skybee.png", caption: "SkyBee Interceptor Overview" },
        { image: "/assets/experience/skybee_2.png", caption: "SkyBee Team Members" },
        { image: "/assets/experience/FA_Skybee_team_member.jpg", caption: "SkyBee Team Members" },
        { image: "/assets/experience/FA_Presenting_Project_Homeland_Defense_Interceptor.jpg", caption: "Presenting SkyBee Interceptor in front of Air Chief, BDAF" }
      ],
      tags: ["Systems Engineering", "Technical Leadership", "Deadline Management", "Aerodynamic Design", "Structural Analysis"]
    },
    {
      category: "Leadership & R&D",
      categoryBadge: "🛸 Technical Leadership",
      badgeStyle: styles.badgeBlue,
      organization: "Drone Club (AAUB)",
      role: "Vice President — Projects & Publications",
      duration: "Jun 2023 – Sep 2025",
      location: "Lalmonirhat, Bangladesh",
      highlight: "Organized Impulse 1.0 (700+ Participants) · Trained 50+ Students in UAVs",
      description: [
        "Designed and built multiple UAV platforms including quadcopters, photography drones, and FPV racing drones, with hands-on experience in flight control and embedded systems.",
        "Organized Impulse 1.0, a national technology festival with 700+ participants, managing logistics, sponsorship, and program committees.",
        "Conducted workshops training 50+ students in UAV development, flight control algorithms, and autonomous navigation, building a community of practice in aerospace engineering."
      ],
      logo: "/assets/experience/drone_club.jpg",
      images: [
        { image: "/assets/experience/drone_club_2.jpg", caption: "Impulse 1.0 Event Organization" },
        { image: "/assets/experience/drone_club_3.jpg", caption: "Hands-on UAV Workshop" }
      ],
      tags: ["Workshop Design", "Mentorship", "Community Building", "UAV Development", "Embedded Systems"]
    },
    {
      category: "Leadership & R&D",
      categoryBadge: "💻 Web & IT Leadership",
      badgeStyle: styles.badgePurple,
      organization: "Adventure & Photography Club (AAUB)",
      role: "Head of IT & Communication",
      duration: "Jun 2024 – Sep 2025",
      location: "Lalmonirhat, Bangladesh",
      highlight: "Full-Stack React.js Club Portal Development & Digital Infrastructure",
      description: [
        "Developed and hosted the club website using React.js on Netlify.",
        "Managed IT infrastructure and coordinated event technology solutions.",
        "Led the team in improving the club's digital presence and online engagement."
      ],
      logo: "/assets/experience/aapc.jpg",
      images: [
        {
          image: "/assets/experience/aapc_2.jpg",
          caption: "With Club Panel at Annual Adventure Festival"
        },
        {
          image: "/assets/experience/hoit.jpeg",
          caption: "Head of IT & Communication"
        }
      ],
      tags: ["React.js", "Web Development", "IT Infrastructure", "Digital Communication"]
    }
  ];

  const categories = ['All', 'Research Experience', 'Research Mentorship', 'Industrial Experience', 'Leadership & R&D'];

  const filteredExperiences = activeCategory === 'All'
    ? experiences
    : experiences.filter((exp) => exp.category === activeCategory);

  const getValidPath = (path) => {
    if (!path || typeof path !== 'string') return '';
    if (path.startsWith('http') || path.startsWith('data:') || path.startsWith('/')) return path;
    return `/${path}`;
  };

  return (
    <div className={styles.container}>
      {/* Page Header & Category Filter */}
      <header className={styles.pageHeader}>
        <div className={styles.pageHeaderTop}>
          <div>
            <span className={styles.pageEyebrow}>RESEARCH, MENTORSHIP & LEADERSHIP</span>
            <h1 className={styles.pageTitle}>Professional & Research Experience</h1>
            <p className={styles.pageSubtitle}>
              Chronicle of funded academic research, undergraduate research mentorship, airline MRO internship, and technical organization leadership.
            </p>
          </div>
          <div className={styles.statsRow}>
            <div className={styles.statChip}>
              <span className={styles.statNum}>2</span>
              <span className={styles.statText}>Active Research & Mentor Roles</span>
            </div>
            <div className={styles.statChip}>
              <span className={styles.statNum}>10–15</span>
              <span className={styles.statText}>Students Mentored</span>
            </div>
            <div className={styles.statChip}>
              <span className={styles.statNum}>40+</span>
              <span className={styles.statText}>Aircraft MRO Ops</span>
            </div>
          </div>
        </div>

        <div className={styles.filterBar} role="tablist" aria-label="Filter experience by category">
          {categories.map((cat) => (
            <button
              key={cat}
              type="button"
              role="tab"
              aria-selected={activeCategory === cat}
              className={`${styles.filterBtn} ${activeCategory === cat ? styles.filterBtnActive : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat === 'All' ? `All Roles (${experiences.length})` : cat}
            </button>
          ))}
        </div>
      </header>

      {/* ── EXPERIENCE TIMELINE ── */}
      <section className={styles.timeline}>
        {filteredExperiences.map((exp, idx) => {
          const role = exp.role || '';
          const org = exp.organization || '';
          const duration = exp.duration || '';
          const descArray = Array.isArray(exp.description) ? exp.description : [];
          const tags = exp.tags || [];

          return (
            <article className={styles.card} key={`${org}-${role}-${idx}`}>
              {/* Top Category & Duration Bar */}
              <div className={styles.cardTopBar}>
                <div className={styles.badgeGroup}>
                  <span className={styles.indexPill}>#{String(idx + 1).padStart(2, '0')}</span>
                  {exp.categoryBadge && (
                    <span className={`${styles.categoryBadge} ${exp.badgeStyle || styles.badgeTeal}`}>
                      {exp.categoryBadge}
                    </span>
                  )}
                </div>
                <div className={styles.metaInline}>
                  {duration && <span className={styles.durationPill}>🗓️ {duration}</span>}
                  {exp.location && <span className={styles.locationPill}>📍 {exp.location}</span>}
                </div>
              </div>

              {/* Header: Logo, Role, Organization */}
              <div className={styles.header}>
                {exp.logo ? (
                  <img
                    src={getValidPath(exp.logo)}
                    alt={`${org} logo`}
                    className={styles.logo}
                    loading="lazy"
                  />
                ) : (
                  <div className={styles.logoFallback} aria-hidden="true">
                    {exp.logoFallback || '🔬'}
                  </div>
                )}
                <div className={styles.titleWrapper}>
                  {role && <h2 className={styles.role}>{role}</h2>}
                  {org && <p className={styles.organization}>{org}</p>}
                  {exp.highlight && <p className={styles.highlightNote}>{exp.highlight}</p>}
                </div>
              </div>

              {/* Tags */}
              {tags.length > 0 && (
                <div className={styles.tags}>
                  {tags.map((tag, tIdx) => (
                    <span key={tIdx} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Description Bullets */}
              {descArray.length > 0 && (
                <ul className={styles.bullets}>
                  {descArray.map((bullet, bIdx) => (
                    <li key={bIdx}>{bullet}</li>
                  ))}
                </ul>
              )}

              {/* Image Gallery with Captions */}
              {exp.images && Array.isArray(exp.images) && exp.images.length > 0 && (
                <div className={styles.gallery}>
                  {exp.images.map((img, imgIdx) => {
                    const imagePath =
                      typeof img === 'object'
                        ? getValidPath(img.image || img.src)
                        : getValidPath(img);
                    const imageCaption =
                      typeof img === 'object' ? img.caption : null;

                    return (
                      <figure
                        key={imgIdx}
                        className={styles.galleryFigure}
                        onClick={() => setSelectedImage({ src: imagePath, caption: imageCaption || role, org })}
                        title="Click to enlarge photo"
                      >
                        <div className={styles.galleryImgBox}>
                          <img
                            src={imagePath}
                            alt={imageCaption || `Experience at ${org}`}
                            className={styles.galleryImage}
                            loading="lazy"
                          />
                          <span className={styles.zoomBadge}>🔍 Enlarge</span>
                        </div>
                        {imageCaption && (
                          <figcaption className={styles.galleryCaption}>
                            {imageCaption}
                          </figcaption>
                        )}
                      </figure>
                    );
                  })}
                </div>
              )}

              {/* External Link */}
              {exp.link && (
                <div className={styles.cardFooter}>
                  <a
                    href={exp.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.externalLink}
                  >
                    View {exp.source || 'Organization Profile'} <span aria-hidden="true">↗</span>
                  </a>
                </div>
              )}
            </article>
          );
        })}
      </section>

      {/* Lightbox Modal for Experience Photos */}
      {selectedImage && (
        <div
          className={styles.modalBackdrop}
          onClick={() => setSelectedImage(null)}
          role="dialog"
          aria-modal="true"
          aria-label={selectedImage.caption}
        >
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div>
                <h3 className={styles.modalTitle}>{selectedImage.caption}</h3>
                {selectedImage.org && <p className={styles.modalSub}>{selectedImage.org}</p>}
              </div>
              <button
                type="button"
                className={styles.modalCloseBtn}
                onClick={() => setSelectedImage(null)}
                aria-label="Close photo preview"
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              <img
                src={selectedImage.src}
                alt={selectedImage.caption}
                className={styles.modalImage}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}